import { Button } from "@faire/web--source/slate/Button";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import isChromatic from "chromatic/isChromatic";
import styled, { css, keyframes } from "styled-components";

const SWITCH_TIME = 5;
const ROTATING_WORDS_LENGTH = 5;
const TOTAL_LENGTH = ROTATING_WORDS_LENGTH * SWITCH_TIME;

const FINISH_FADE_IN = 0.1 * SWITCH_TIME;
const START_FADE_OUT = 0.9 * SWITCH_TIME;
const FINISH = 1.0 * SWITCH_TIME;
const POST_FINISH = 1.1 * SWITCH_TIME;

const FINISH_FADE_IN_PCT = ((FINISH_FADE_IN / TOTAL_LENGTH) * 100).toFixed(2);
const START_FADE_OUT_PCT = ((START_FADE_OUT / TOTAL_LENGTH) * 100).toFixed(2);
const FINISH_PCT = ((FINISH / TOTAL_LENGTH) * 100).toFixed(2);
const POST_FINISH_PCT = ((POST_FINISH / TOTAL_LENGTH) * 100).toFixed(2);

const rotateImages = keyframes`
  0% {
    opacity: 0;
    visibility: hidden;
    transform: translateY(30px);
    -webkit-animation: translateY(30px);
    -ms-transform: translateY(30px);
  }
  ${FINISH_FADE_IN_PCT}% {
    opacity: 1;
    visibility: visible;
    transform: translateY(0px);
    -webkit-animation: translateY(0px);
    -ms-transform: translateY(0px);
  }
  ${START_FADE_OUT_PCT}% {
    opacity: 1;
    visibility: visible;
    transform: translateY(0px);
    -webkit-animation: translateY(0px);
    -ms-transform: translateY(0px);
  }
  ${FINISH_PCT}% {
    opacity: 0;
    visibility: hidden;
    transform: translateY(-30px);
    -webkit-animation: translateY(-30px);
    -ms-transform: translateY(-30px);
  }
  ${POST_FINISH_PCT}% {
    opacity: 0;
    visibility: hidden;
    transform: translateY(30px);
    -webkit-animation: translateY(30px);
    -ms-transform: translateY(30px);
  }
`;

const rotateWords = keyframes`
  0% {
    opacity: 0;
    visibility: hidden;
    transform: translateY(30px);
    -webkit-animation: translateY(30px);
    -ms-transform: translateY(30px);
  }
  ${FINISH_FADE_IN_PCT}% {
    opacity: 1;
    visibility: visible;
    transform: translateY(0px);
    -webkit-animation: translateY(0px);
    -ms-transform: translateY(0px);
  }
  ${START_FADE_OUT_PCT}% {
    opacity: 1;
    visibility: visible;
    transform: translateY(0px);
    -webkit-animation: translateY(0px);
    -ms-transform: translateY(0px);
  }
  ${FINISH_PCT}% {
    opacity: 0;
    visibility: hidden;
    transform: translateY(-30px);
    -webkit-animation: translateY(-30px);
    -ms-transform: translateY(-30px);
  }
  ${POST_FINISH_PCT}% {
    opacity: 0;
    visibility: hidden;
    transform: translateY(30px);
    -webkit-animation: translateY(30px);
    -ms-transform: translateY(30px);
  }
`;

const WORD_PERFECT_POSITIONING = ["57px", "89px", "44px", "98px", "92px"];
const animateWordPerfect = keyframes`
  0% {
    left: ${WORD_PERFECT_POSITIONING[0]};
  }
  17.5% {
    left: ${WORD_PERFECT_POSITIONING[0]};
  }

  20% {
    left: ${WORD_PERFECT_POSITIONING[1]};
  }
  37.5% {
    left: ${WORD_PERFECT_POSITIONING[1]};
  }

  40% {
    left: ${WORD_PERFECT_POSITIONING[2]};
  }
  57.5% {
    left: ${WORD_PERFECT_POSITIONING[2]};
  }

  60% {
    left: ${WORD_PERFECT_POSITIONING[3]};
  }
  77.5% {
    left: ${WORD_PERFECT_POSITIONING[3]};
  }

  80% {
    left: ${WORD_PERFECT_POSITIONING[4]};
  }
  97.5% {
    left: ${WORD_PERFECT_POSITIONING[4]};
  }

  100% {
    left: ${WORD_PERFECT_POSITIONING[0]};
  }
`;

const imageAnimationCSS = css<{ $index: number }>`
  visibility: hidden;
  opacity: 0;
  animation: ${rotateImages} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
  -webkit-animation: ${rotateImages} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
  -ms-animation: ${rotateImages} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
`;

export const AnimatedImageSection = styled.div<{ $index: number }>`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  ${isChromatic() ? "" : imageAnimationCSS}
`;

const linkAnimationCss = css<{ $index: number; $linkPositioning?: string[] }>`
  visibility: hidden;
  opacity: 0;
  animation: ${rotateWords} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
  -webkit-animation: ${rotateWords} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
  -ms-animation: ${rotateWords} ${TOTAL_LENGTH}s linear infinite
    ${({ $index }) => $index * SWITCH_TIME}s;
`;

export const RotatingLink = styled(Button).attrs({
  variant: "tertiary",
})<{
  $index: number;
  $linkPositioning?: string[];
}>`
  position: absolute;
  text-decoration-thickness: 0.1em;
  left: ${(props) => props.$linkPositioning?.[props.$index] ?? 0};
  display: inline;
  width: 100%;
  ${responsiveMediaQueries.xLargeAndAbove} {
    width: unset;
  }
  overflow: hidden;
  &:hover {
    text-decoration-thickness: 0.1em;
  }
  ${isChromatic() ? "" : linkAnimationCss}
`;

export const BannerContentTypography = styled(Typography).attrs({
  variant: {
    mobileAndAbove: "displayMSerifRegular",
    desktopAndAbove: "displayLSerifRegular",
  },
})``;

export const NonEngContentWrapper = styled.div`
  display: grid;
  place-items: center;
  grid-template-areas: "non-eng-content";
  padding: ${getSpacing("4x")};
  width: 100%;
  ${responsiveMediaQueries.tabletAndAbove} {
    max-width: 430px;
  }
  ${responsiveMediaQueries.xLargeAndAbove} {
    width: 100%;
    max-width: 650px;
  }
  text-align: center;
`;

export const RotatingNonEngContent = styled(BannerContentTypography)<{
  index: number;
}>`
  display: inline;
  overflow: hidden;
  visibility: hidden;
  opacity: 0;
  width: 100%;
  ${responsiveMediaQueries.xLargeAndAbove} {
    width: unset;
  }
  animation: ${rotateWords} ${TOTAL_LENGTH}s linear infinite
    ${({ index }) => index * SWITCH_TIME}s;
  -webkit-animation: ${rotateWords} ${TOTAL_LENGTH}s linear infinite
    ${({ index }) => index * SWITCH_TIME}s;
  -ms-animation: ${rotateWords} ${TOTAL_LENGTH}s linear infinite
    ${({ index }) => index * SWITCH_TIME}s;
  position: relative;
  grid-area: non-eng-content;
`;

export const AnimatedWordPerfect = styled.div`
  width: 100%;
  text-align: center;
  ${responsiveMediaQueries.xLargeAndAbove} {
    width: unset;
    position: absolute;
    overflow: hidden;
    display: inline;
  }
  animation: ${animateWordPerfect} ${TOTAL_LENGTH}s linear infinite;
`;
